// UXPolishSystem.js
// Enterprise-grade UX polish and micro-interactions
// Provides comprehensive accessibility, keyboard navigation, and visual feedback

import { MobileDetectionService } from './MobileDetectionService.js';
import { professionalAnimations } from './ProfessionalAnimationSystem.js';
import { professionalNotifications } from './ProfessionalNotificationSystem.js';
import { performanceMonitoring } from './PerformanceMonitoringSystem.js';

/**
 * UX polish system
 * Provides enterprise-grade micro-interactions, accessibility, and visual feedback
 * Ensures consistent, polished user experience across all scenes
 */
export class UXPolishSystem {
    static _instance = null;
    
    /**
     * Singleton pattern for consistent UX management
     */
    static getInstance() {
        if (!UXPolishSystem._instance) {
            UXPolishSystem._instance = new UXPolishSystem();
        }
        return UXPolishSystem._instance;
    }
    
    constructor() {
        if (UXPolishSystem._instance) {
            return UXPolishSystem._instance;
        }
        
        this.polishedElements = new Set();
        this.keyboardNavigationActive = false;
        this.touchSupported = 'ontouchstart' in window;
        
        this._injectPolishCSS();
        this._setupGlobalInteractions();
        this._setupAccessibility();
        
        UXPolishSystem._instance = this;
    }
    
    /**
     * Apply UX polish to a scene
     * @param {string} sceneKey - Scene identifier
     * @param {string} overlayId - DOM overlay ID
     */
    polishScene(sceneKey, overlayId = 'auth-menu-dom-overlay') {
        const overlay = document.getElementById(overlayId);
        if (!overlay) return;
        
        const isMobile = MobileDetectionService.isMobile();
        console.log(`✨ UXPolishSystem: Applying polish to ${sceneKey} (${isMobile ? 'mobile' : 'desktop'})`);
        
        // Apply micro-interactions
        this._applyMicroInteractions(overlay, isMobile);
        
        // Setup keyboard navigation
        this._setupKeyboardNavigation(overlay);
        
        // Apply accessibility enhancements
        this._applyAccessibilityEnhancements(overlay);
        
        // Setup touch optimizations for mobile
        if (isMobile) {
            this._applyTouchOptimizations(overlay);
        }
        
        // Apply visual feedback
        this._applyVisualFeedback(overlay);
        
        this.polishedElements.add(sceneKey);
        console.log(`✅ UX polish applied to ${sceneKey}`);
    }
    
    /**
     * Remove UX polish from a scene
     * @param {string} sceneKey - Scene identifier
     */
    removePolish(sceneKey) {
        if (!this.polishedElements.has(sceneKey)) return;
        
        console.log(`🧹 UXPolishSystem: Removing polish from ${sceneKey}`);
        
        // CRITICAL FIX: Actually remove tooltip DOM elements
        const tooltips = document.querySelectorAll('.ux-tooltip');
        tooltips.forEach(tooltip => {
            tooltip.remove();
            console.log('🧹 Removed stuck tooltip');
        });
        
        this.polishedElements.delete(sceneKey);
    }
    
    /**
     * Apply micro-interactions to elements
     * @private
     */
    _applyMicroInteractions(overlay, isMobile) {
        // Navigation items
        const navItems = overlay.querySelectorAll('.nav-item');
        navItems.forEach(item => {
            this._enhanceNavigationItem(item, isMobile);
        });
        
        // Buttons
        const buttons = overlay.querySelectorAll('button, .btn');
        buttons.forEach(button => {
            this._enhanceButton(button, isMobile);
        });
        
        // Cards
        const cards = overlay.querySelectorAll('.kpi-card, .chart-card, .game-card, .stats-card, .leaderboard-card');
        cards.forEach(card => {
            this._enhanceCard(card, isMobile);
        });
        
        // Form inputs
        const inputs = overlay.querySelectorAll('input, select, textarea');
        inputs.forEach(input => {
            this._enhanceFormInput(input, isMobile);
        });
    }
    
    /**
     * Enhance navigation item with micro-interactions
     * @private
     */
    _enhanceNavigationItem(item, isMobile) {
        if (item.dataset.enhanced) return;
        
        // Hover effects
        item.addEventListener('mouseenter', () => {
            if (!isMobile) {
                item.style.transform = 'translateX(4px)';
                item.style.background = 'rgba(255, 255, 255, 0.08)';
            }
        });
        
        item.addEventListener('mouseleave', () => {
            if (!isMobile) {
                item.style.transform = '';
                item.style.background = '';
            }
        });
        
        // Click feedback
        item.addEventListener('click', () => {
            const startTime = performance.now();
            
            // Visual feedback
            item.style.transform = 'scale(0.98)';
            setTimeout(() => {
                item.style.transform = '';
            }, 150);
            
            // Performance tracking
            setTimeout(() => {
                const responseTime = performance.now() - startTime;
                performanceMonitoring.recordInteraction('navigation', responseTime, item.dataset.action || item.dataset.game);
            }, 10);
        });
        
        item.dataset.enhanced = 'true';
    }
    
    /**
     * Enhance button with micro-interactions
     * @private
     */
    _enhanceButton(button, isMobile) {
        if (button.dataset.enhanced) return;
        
        // Add professional button class
        button.classList.add('btn-professional');
        
        // Hover effects (desktop only)
        if (!isMobile) {
            button.addEventListener('mouseenter', () => {
                button.style.transform = 'translateY(-2px)';
                button.style.boxShadow = '0 8px 25px rgba(0, 212, 255, 0.3)';
            });
            
            button.addEventListener('mouseleave', () => {
                button.style.transform = '';
                button.style.boxShadow = '';
            });
        }
        
        // Click feedback
        button.addEventListener('click', (e) => {
            const startTime = performance.now();
            
            // Create ripple effect
            this._createRippleEffect(button, e);
            
            // Performance tracking
            setTimeout(() => {
                const responseTime = performance.now() - startTime;
                performanceMonitoring.recordInteraction('button', responseTime, button.textContent || button.dataset.action);
            }, 10);
        });
        
        button.dataset.enhanced = 'true';
    }
    
    /**
     * Enhance card with micro-interactions
     * @private
     */
    _enhanceCard(card, isMobile) {
        if (card.dataset.enhanced) return;
        
        // Hover effects (desktop only)
        if (!isMobile) {
            card.addEventListener('mouseenter', () => {
                card.style.transform = 'translateY(-4px)';
                card.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.2)';
            });
            
            card.addEventListener('mouseleave', () => {
                card.style.transform = '';
                card.style.boxShadow = '';
            });
        }
        
        card.dataset.enhanced = 'true';
    }
    
    /**
     * Enhance form input with micro-interactions
     * @private
     */
    _enhanceFormInput(input, isMobile) {
        if (input.dataset.enhanced) return;
        
        // Focus effects
        input.addEventListener('focus', () => {
            input.style.borderColor = '#00d4ff';
            input.style.boxShadow = '0 0 0 3px rgba(0, 212, 255, 0.1)';
        });
        
        input.addEventListener('blur', () => {
            input.style.borderColor = '';
            input.style.boxShadow = '';
        });
        
        // Validation feedback
        input.addEventListener('invalid', () => {
            input.style.borderColor = '#ef4444';
            input.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
        });
        
        input.dataset.enhanced = 'true';
    }
    
    /**
     * Create ripple effect on button click
     * @private
     */
    _createRippleEffect(button, event) {
        const ripple = document.createElement('span');
        const rect = button.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = event.clientX - rect.left - size / 2;
        const y = event.clientY - rect.top - size / 2;
        
        ripple.style.cssText = `
            position: absolute;
            width: ${size}px;
            height: ${size}px;
            left: ${x}px;
            top: ${y}px;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 50%;
            transform: scale(0);
            animation: ripple ${professionalAnimations.getDuration('normal')}ms ease-out;
            pointer-events: none;
            z-index: 1;
        `;
        
        // Ensure button has relative positioning
        const position = window.getComputedStyle(button).position;
        if (position === 'static') {
            button.style.position = 'relative';
        }
        button.style.overflow = 'hidden';
        
        button.appendChild(ripple);
        
        // Remove ripple after animation
        setTimeout(() => {
            if (ripple.parentNode) {
                ripple.parentNode.removeChild(ripple);
            }
        }, professionalAnimations.getDuration('normal'));
    }
    
    /**
     * Setup keyboard navigation
     * @private
     */
    _setupKeyboardNavigation(overlay) {
        // Make interactive elements focusable
        const interactiveElements = overlay.querySelectorAll('.nav-item, button, input, select, textarea, [data-action], [data-game]');
        
        interactiveElements.forEach((element, index) => {
            if (!element.tabIndex && element.tabIndex !== 0) {
                element.tabIndex = 0;
            }
            
            // Add focus indicators
            element.addEventListener('focus', () => {
                element.classList.add('focus-visible');
                this.keyboardNavigationActive = true;
            });
            
            element.addEventListener('blur', () => {
                element.classList.remove('focus-visible');
            });
            
            // Keyboard activation
            element.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    element.click();
                }
            });
        });
        
        // Setup arrow key navigation for nav items
        this._setupArrowKeyNavigation(overlay);
    }
    
    /**
     * Setup arrow key navigation
     * @private
     */
    _setupArrowKeyNavigation(overlay) {
        const navItems = Array.from(overlay.querySelectorAll('.nav-item'));
        
        overlay.addEventListener('keydown', (e) => {
            if (!this.keyboardNavigationActive) return;
            
            const focusedElement = document.activeElement;
            const currentIndex = navItems.indexOf(focusedElement);
            
            if (currentIndex === -1) return;
            
            let targetIndex = currentIndex;
            
            switch (e.key) {
                case 'ArrowUp':
                    e.preventDefault();
                    targetIndex = currentIndex > 0 ? currentIndex - 1 : navItems.length - 1;
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    targetIndex = currentIndex < navItems.length - 1 ? currentIndex + 1 : 0;
                    break;
                case 'Home':
                    e.preventDefault();
                    targetIndex = 0;
                    break;
                case 'End':
                    e.preventDefault();
                    targetIndex = navItems.length - 1;
                    break;
            }
            
            if (targetIndex !== currentIndex) {
                navItems[targetIndex].focus();
            }
        });
    }
    
    /**
     * Apply accessibility enhancements
     * @private
     */
    _applyAccessibilityEnhancements(overlay) {
        // Add ARIA labels where missing
        const navItems = overlay.querySelectorAll('.nav-item');
        navItems.forEach(item => {
            if (!item.getAttribute('aria-label')) {
                const label = item.querySelector('.nav-label')?.textContent || 
                            item.dataset.action || 
                            item.dataset.game || 
                            'Navigation item';
                item.setAttribute('aria-label', label);
            }
        });
        
        // Add role attributes
        const navigation = overlay.querySelector('.nav-menu');
        if (navigation && !navigation.getAttribute('role')) {
            navigation.setAttribute('role', 'navigation');
            navigation.setAttribute('aria-label', 'Main navigation');
        }
        
        // Add live regions for dynamic content
        const contentAreas = overlay.querySelectorAll('.dashboard-content, .content-area');
        contentAreas.forEach(area => {
            if (!area.getAttribute('aria-live')) {
                area.setAttribute('aria-live', 'polite');
            }
        });
        
        // Improve button accessibility
        const buttons = overlay.querySelectorAll('button');
        buttons.forEach(button => {
            if (!button.getAttribute('aria-label') && !button.textContent.trim()) {
                const action = button.dataset.action || 'button';
                button.setAttribute('aria-label', action);
            }
        });
    }
    
    /**
     * Apply touch optimizations for mobile
     * @private
     */
    _applyTouchOptimizations(overlay) {
        // Ensure minimum touch target size (44px)
        const touchTargets = overlay.querySelectorAll('.nav-item, button, .btn, [data-action], [data-game]');
        
        touchTargets.forEach(target => {
            const rect = target.getBoundingClientRect();
            
            if (rect.width < 44 || rect.height < 44) {
                target.style.minWidth = '44px';
                target.style.minHeight = '44px';
                target.style.display = 'flex';
                target.style.alignItems = 'center';
                target.style.justifyContent = 'center';
            }
        });
        
        // Add touch feedback
        touchTargets.forEach(target => {
            target.addEventListener('touchstart', () => {
                target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            }, { passive: true });
            
            target.addEventListener('touchend', () => {
                setTimeout(() => {
                    target.style.backgroundColor = '';
                }, 150);
            }, { passive: true });
        });
    }
    
    /**
     * Apply visual feedback enhancements
     * @private
     */
    _applyVisualFeedback(overlay) {
        // Add loading states to async actions
        const asyncButtons = overlay.querySelectorAll('[data-action="analytics"], [data-action="hall-of-fame"], [data-action="profile"]');
        
        asyncButtons.forEach(button => {
            button.addEventListener('click', () => {
                this._showButtonLoading(button);
            });
        });
        
        // Add success feedback to form submissions
        const forms = overlay.querySelectorAll('form');
        forms.forEach(form => {
            form.addEventListener('submit', () => {
                professionalNotifications.info('Processing...', { duration: 2000 });
            });
        });
        
        // Add hover previews for navigation items
        const navItems = overlay.querySelectorAll('.nav-item[data-action]');
        navItems.forEach(item => {
            this._addNavigationPreview(item);
        });
    }
    
    /**
     * Show loading state on button
     * @private
     */
    _showButtonLoading(button) {
        const originalText = button.textContent;
        const originalHTML = button.innerHTML;
        
        button.innerHTML = `
            <span style="display: flex; align-items: center; gap: 8px;">
                <span style="width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top: 2px solid #00d4ff; border-radius: 50%; animation: spin 1s linear infinite;"></span>
                Loading...
            </span>
        `;
        
        button.disabled = true;
        
        // Reset after transition (assuming 1 second max)
        setTimeout(() => {
            button.innerHTML = originalHTML;
            button.disabled = false;
        }, 1000);
    }
    
    /**
     * Add navigation preview on hover
     * @private
     */
    _addNavigationPreview(item) {
        const isMobile = MobileDetectionService.isMobile();
        if (isMobile) return; // Skip previews on mobile
        
        let previewTimeout;
        
        item.addEventListener('mouseenter', () => {
            previewTimeout = setTimeout(() => {
                const action = item.dataset.action;
                const previewText = this._getNavigationPreview(action);
                
                if (previewText) {
                    this._showTooltip(item, previewText);
                }
            }, 800); // Show after 800ms hover
        });
        
        item.addEventListener('mouseleave', () => {
            clearTimeout(previewTimeout);
            this._hideTooltip(item);
        });
    }
    
    /**
     * Get navigation preview text
     * @private
     */
    _getNavigationPreview(action) {
        const previews = {
            'dashboard': 'View executive dashboard with KPIs and performance metrics',
            'analytics': 'Detailed performance analytics and insights',
            'hall-of-fame': 'Leaderboards and competitive rankings',
            'game-center': 'Training modules and game selection',
            'profile': 'Profile management and account settings',
            'admin-panel': 'Administrative controls and system management'
        };
        
        return previews[action] || null;
    }
    
    /**
     * Show tooltip
     * @private
     */
    _showTooltip(element, text) {
        const tooltip = document.createElement('div');
        tooltip.className = 'ux-tooltip';
        tooltip.textContent = text;
        
        // Position tooltip
        const rect = element.getBoundingClientRect();
        tooltip.style.cssText = `
            position: fixed;
            top: ${rect.top - 40}px;
            left: ${rect.left}px;
            background: rgba(0, 0, 0, 0.9);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            z-index: 60000;
            max-width: 200px;
            white-space: nowrap;
            pointer-events: none;
            opacity: 0;
            transform: translateY(10px);
            transition: all 200ms ease;
        `;
        
        document.body.appendChild(tooltip);
        element.dataset.tooltipId = tooltip.id = `tooltip-${Date.now()}`;
        
        // Animate in
        requestAnimationFrame(() => {
            tooltip.style.opacity = '1';
            tooltip.style.transform = 'translateY(0)';
        });
    }
    
    /**
     * Hide tooltip
     * @private
     */
    _hideTooltip(element) {
        const tooltipId = element.dataset.tooltipId;
        if (!tooltipId) return;
        
        const tooltip = document.getElementById(tooltipId);
        if (!tooltip) return;
        
        tooltip.style.opacity = '0';
        tooltip.style.transform = 'translateY(10px)';
        
        setTimeout(() => {
            if (tooltip.parentNode) {
                tooltip.parentNode.removeChild(tooltip);
            }
        }, 200);
        
        delete element.dataset.tooltipId;
    }
    
    /**
     * Setup global interactions
     * @private
     */
    _setupGlobalInteractions() {
        // Detect keyboard navigation
        document.addEventListener('keydown', () => {
            this.keyboardNavigationActive = true;
        });
        
        document.addEventListener('mousedown', () => {
            this.keyboardNavigationActive = false;
        });
        
        // Global keyboard shortcuts
        document.addEventListener('keydown', (e) => {
            // ESC to dismiss notifications
            if (e.key === 'Escape') {
                professionalNotifications.dismissAll();
            }
            
            // Ctrl/Cmd + K for quick navigation (future enhancement)
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                console.log('🔍 Quick navigation shortcut triggered (future enhancement)');
            }
        });
    }
    
    /**
     * Setup accessibility features
     * @private
     */
    _setupAccessibility() {
        // Skip to main content link
        this._addSkipLink();
        
        // Focus management
        this._setupFocusManagement();
        
        // Screen reader announcements
        this._setupScreenReaderSupport();
    }
    
    /**
     * Add skip to main content link
     * @private
     */
    _addSkipLink() {
        if (document.getElementById('skip-link')) return;
        
        const skipLink = document.createElement('a');
        skipLink.id = 'skip-link';
        skipLink.href = '#main-content';
        skipLink.textContent = 'Skip to main content';
        skipLink.style.cssText = `
            position: fixed;
            top: -100px;
            left: 20px;
            background: #00d4ff;
            color: #0f172a;
            padding: 8px 16px;
            border-radius: 6px;
            text-decoration: none;
            font-weight: 600;
            z-index: 100000;
            transition: top 200ms ease;
        `;
        
        skipLink.addEventListener('focus', () => {
            skipLink.style.top = '20px';
        });
        
        skipLink.addEventListener('blur', () => {
            skipLink.style.top = '-100px';
        });
        
        document.body.insertBefore(skipLink, document.body.firstChild);
    }
    
    /**
     * Setup focus management
     * @private
     */
    _setupFocusManagement() {
        // Focus trapping for modals (future enhancement)
        // Return focus to triggering element after modal close
    }
    
    /**
     * Setup screen reader support
     * @private
     */
    _setupScreenReaderSupport() {
        // Announce page changes
        const announcer = document.createElement('div');
        announcer.id = 'screen-reader-announcer';
        announcer.setAttribute('aria-live', 'assertive');
        announcer.setAttribute('aria-atomic', 'true');
        announcer.style.cssText = `
            position: absolute;
            left: -10000px;
            width: 1px;
            height: 1px;
            overflow: hidden;
        `;
        
        document.body.appendChild(announcer);
    }
    
    /**
     * Announce to screen readers
     * @param {string} message - Message to announce
     */
    announceToScreenReader(message) {
        const announcer = document.getElementById('screen-reader-announcer');
        if (announcer) {
            announcer.textContent = message;
            
            // Clear after announcement
            setTimeout(() => {
                announcer.textContent = '';
            }, 1000);
        }
    }
    
    /**
     * Inject UX polish CSS
     * @private
     */
    _injectPolishCSS() {
        if (document.getElementById('ux-polish-css')) return;
        
        const polishCSS = `
            <style id="ux-polish-css">
                /* UX Polish System - Enterprise Grade */
                
                /* Enhanced button styling */
                .btn-professional {
                    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    overflow: hidden;
                }
                
                /* Focus indicators */
                .focus-visible {
                    outline: 2px solid #00d4ff !important;
                    outline-offset: 2px !important;
                    box-shadow: 0 0 0 4px rgba(0, 212, 255, 0.2) !important;
                }
                
                /* Smooth transitions for all interactive elements */
                .nav-item, button, .btn, input, select, textarea, .card {
                    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
                }
                
                /* Enhanced hover states */
                .nav-item:hover {
                    background: rgba(255, 255, 255, 0.08) !important;
                }
                
                /* Form enhancements */
                input:focus, select:focus, textarea:focus {
                    border-color: #00d4ff !important;
                    box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1) !important;
                }
                
                input:invalid {
                    border-color: #ef4444 !important;
                    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
                }
                
                /* Tooltip styling */
                .ux-tooltip {
                    position: fixed;
                    background: rgba(0, 0, 0, 0.9);
                    color: white;
                    padding: 8px 12px;
                    border-radius: 6px;
                    font-size: 12px;
                    z-index: 60000;
                    max-width: 200px;
                    pointer-events: none;
                    transition: all 200ms ease;
                }
                
                /* Ripple animation */
                @keyframes ripple {
                    to {
                        transform: scale(4);
                        opacity: 0;
                    }
                }
                
                /* Skip link */
                #skip-link:focus {
                    top: 20px !important;
                }
                
                /* Touch target optimization */
                @media (max-width: 768px) {
                    .nav-item, button, .btn, [data-action], [data-game] {
                        min-width: 44px;
                        min-height: 44px;
                    }
                }
                
                /* High contrast mode support */
                @media (prefers-contrast: high) {
                    .nav-item, button, .btn {
                        border: 2px solid currentColor;
                    }
                    
                    .focus-visible {
                        outline-width: 3px !important;
                    }
                }
                
                /* Reduced motion support */
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: 0.01ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: 0.01ms !important;
                    }
                }
            </style>
        `;
        
        document.head.insertAdjacentHTML('beforeend', polishCSS);
        console.log('🎨 UX polish system CSS injected');
    }
    
    /**
     * Get UX metrics
     * @returns {Object} - UX performance metrics
     */
    getUXMetrics() {
        return {
            polishedScenes: Array.from(this.polishedElements),
            keyboardNavigationActive: this.keyboardNavigationActive,
            touchSupported: this.touchSupported,
            accessibilityFeatures: {
                skipLink: !!document.getElementById('skip-link'),
                screenReaderSupport: !!document.getElementById('screen-reader-announcer'),
                focusManagement: true,
                keyboardNavigation: true
            }
        };
    }
    
    /**
     * Reset UX polish (for debugging)
     */
    resetPolish() {
        console.log('🔄 UXPolishSystem: Resetting polish');
        this.polishedElements.clear();
        this.keyboardNavigationActive = false;
    }
}

// Export singleton instance
export const uxPolish = UXPolishSystem.getInstance();
export default uxPolish;





